import type { CuratedCoverAsset, EventCoverCategory } from "@/lib/events/curated-covers"
import { MAX_IMAGE_UPLOAD_BYTES } from "@/lib/events/image-upload-constants"
import {
  CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT,
  cloudflareImageDeliveryUrl,
} from "@/lib/media/cloudflare-urls"

/** Same bytes cap as album images; kept for existing imports. */
export const MAX_EVENT_COVER_UPLOAD_BYTES = MAX_IMAGE_UPLOAD_BYTES
export { MAX_IMAGE_UPLOAD_BYTES }

export const ALLOWED_EVENT_COVER_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const

export type AllowedEventCoverMime = (typeof ALLOWED_EVENT_COVER_MIME_TYPES)[number]

export type EventCoverSelection =
  | {
      kind: "curated"
      curatedId: string
      imageUrl: string
      alt: string
      category: EventCoverCategory
    }
  | {
      kind: "upload"
      imageUrl: string
      cloudflareImageId?: string
      /** Local file picked before the event exists; uploaded on save/publish only. Client-only. */
      pendingFile?: File
      fileName?: string
      mimeType?: string
      sizeBytes?: number
    }

/**
 * Serializable shape for future API / DB (v1).
 * `imageUrl` is the resolved display URL at save time; uploads may also set `cloudflareImageId`.
 */
export type SerializedEventCoverV1 =
  | {
      v: 1
      source: "curated"
      curatedId: string
      imageUrl: string
      category: EventCoverCategory
    }
  | {
      v: 1
      source: "upload"
      imageUrl: string
      cloudflareImageId?: string
      fileName?: string
      mimeType?: string
      sizeBytes?: number
    }

export function curatedAssetToSelection(
  asset: CuratedCoverAsset,
): EventCoverSelection {
  return {
    kind: "curated",
    curatedId: asset.id,
    imageUrl: asset.src,
    alt: asset.alt,
    category: asset.type,
  }
}

export function serializeEventCoverSelection(
  selection: EventCoverSelection | null,
): SerializedEventCoverV1 | null {
  if (!selection) return null
  if (selection.kind === "curated") {
    return {
      v: 1,
      source: "curated",
      curatedId: selection.curatedId,
      imageUrl: selection.imageUrl,
      category: selection.category,
    }
  }
  return {
    v: 1,
    source: "upload",
    imageUrl: selection.imageUrl,
    cloudflareImageId: selection.cloudflareImageId,
    fileName: selection.fileName,
    mimeType: selection.mimeType,
    sizeBytes: selection.sizeBytes,
    // pendingFile intentionally omitted — not serializable
  }
}

export function validateCoverUploadFile(file: File): string | null {
  if (!ALLOWED_EVENT_COVER_MIME_TYPES.includes(file.type as AllowedEventCoverMime)) {
    return "Use a JPEG, PNG, WebP, or GIF image."
  }
  if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
    return `Image must be under ${Math.round(MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024))} MB.`
  }
  return null
}

export function resolveUploadedCoverDisplayUrl(imageId: string): string | null {
  return cloudflareImageDeliveryUrl(
    imageId.trim(),
    CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT,
  )
}
